import './App.scss';

import React, {createContext, useEffect, useMemo, useState} from 'react';
import { v4 as uuidv4 } from 'uuid';

import Cookies from 'js-cookie';

import Header from './containers/Header';
import Body from './containers/Body';
import Footer from './containers/Footer';
import LoginWallpaper from './containers/LoginWallpaper';
import AddLink from "./components/AddLink";
import ProfileManager from "./components/ProfileManager";
import ErrorList from './components/ErrorList';

import {
	ALL_PROFILE,
	filterLinksByProfile,
	getCategories,
	getCategoryIdxForProfile,
	getLinks,
	getProfiles,
	includesProfile,
	isAllProfile,
	sortCategories
} from "./tools";
import {ensureDefaultProfiles, login, logout} from "./firebaseTools";

export const StateContext = createContext(null);

const App = () => {
	const [showLoginForm, setShowLoginForm] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [showAddLink, setShowAddLink] = useState(false);
	const [showProfileManager, setShowProfileManager] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [profiles, setProfiles] = useState([ALL_PROFILE]);
	const [profile, setProfile] = useState(ALL_PROFILE);
	const [allCategories, setAllCategories] = useState([]);
	const [allLinks, setAllLinks] = useState([]);
	const [categories, setCategories] = useState([]);
	const [links, setLinks] = useState([]);
	const [errors, setErrors] = useState([]);

	const addError = (text) => {
		setErrors(errs => [
			...errs,
			{ id: uuidv4(), text }
		]);
	};

	const removeError = (id) => {
		setErrors(errs => errs.filter(e => e.id !== id));
	};

	const clearAllErrors = () => {
		setErrors([]);
	};

	const setCookies = (user) => {
		if (user) {
			Cookies.set('authUser', JSON.stringify({
				uid: user.uid,
				email: user.email
			}), {
				expires: 7,
				secure: true,
				sameSite: 'strict',
				path: '/'
			});
		} else {
			Cookies.remove('authUser', {path: '/'});
		}
	};

	useEffect(() => {
		const existingCookie = Cookies.get('authUser');
		if (existingCookie) {
			try {
				const userData = JSON.parse(existingCookie);
				setIsLoggedIn(!!userData);
			} catch (error) {
				addError(`Error parsing auth cookie: ${error.message}`);
				Cookies.remove('authUser', {path: '/'});
			}
		}
	}, []);

	useEffect(() => {
		if (!isLoggedIn) {
			setProfiles([ALL_PROFILE]);
			setProfile(ALL_PROFILE);
			setAllCategories([]);
			setAllLinks([]);
			setCategories([]);
			setLinks([]);
			return;
		}

		const loadData = async () => {
			await ensureDefaultProfiles(addError);
			await getProfiles(setProfiles, setProfile, addError);
			await getCategories(setAllCategories, addError, ALL_PROFILE);
			await getLinks(setAllLinks, addError);
		};

		loadData().then();
	}, [isLoggedIn]);

	useEffect(() => {
		const activeProfile = profile ?? ALL_PROFILE;
		const filteredLinks = filterLinksByProfile(allLinks, activeProfile);
		const categorySource = isAllProfile(activeProfile)
			? allCategories
			: allCategories.filter((category) => includesProfile(category, activeProfile.id));

		const displayCategories = sortCategories(
			categorySource.map((category) => ({
				...category,
				idx: getCategoryIdxForProfile(category, activeProfile.id)
			}))
		);

		setLinks(filteredLinks);
		setCategories(displayCategories);
	}, [allCategories, allLinks, profile]);

	const onLogin = async (email, password) => {
		const loggedIn = await login(email, password, addError);
		if (loggedIn?.user) {
			setCookies(loggedIn.user);
			setIsLoggedIn(true);
			setShowLoginForm(false);
		}
	};

	const handleLogout = async () => {
		const loggedOut = await logout(addError);
		if (loggedOut) {
			setIsLoggedIn(false);
			setCookies(null);
		}
	};

	const handleSwitchProfile = () => {
		if (!profiles.length) return;
		const currentIdx = profiles.findIndex((item) => item.id === profile?.id);
		const nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % profiles.length;
		const nextProfile = profiles[nextIdx] ?? ALL_PROFILE;
		setProfile(nextProfile);
		Cookies.set("profileId", nextProfile.id, {expires: 7, path: "/"});
	};

	const contextValue = useMemo(() => ({
		addError,
		profiles,
		setProfiles,
		profile,
		setProfile,
		allCategories,
		setAllCategories,
		allLinks,
		setAllLinks
	}), [profiles, profile, allCategories, allLinks]);

	return (
		<StateContext.Provider value={contextValue}>
			<div className="app">
				<ErrorList
					errors={errors}
					onRemove={removeError}
					onClearAll={clearAllErrors}
				/>
				{!isLoggedIn ? (
					<LoginWallpaper
						onLogin={onLogin}
						showLoginForm={showLoginForm}
						setShowLoginForm={setShowLoginForm}
					/>
				) : (
					<>
						<div className="app-header">
							<Header
								isEdit={isEdit}
								setIsEdit={setIsEdit}
								onLogout={handleLogout}
								onSwitchProfile={handleSwitchProfile}
								setShowProfileManager={setShowProfileManager}
								setShowAddLink={setShowAddLink}
								showAddLink={showAddLink}
								profile={profile}
							/>
						</div>
						<div className="app-body">
							{showAddLink && (
								<AddLink
									categories={categories}
									setLinks={setLinks}
									setShowAddLink={setShowAddLink}
								/>
							)}
							{showProfileManager && (
								<ProfileManager
									onClose={() => setShowProfileManager(false)}
								/>
							)}
							<Body
								isEdit={isEdit}
								categories={categories}
								links={links}
								activeProfile={profile}
								addError={addError}
							/>
						</div>
						<div className="app-footer">
							<Footer/>
						</div>
					</>
				)}
			</div>
		</StateContext.Provider>
	);
};

export default App;
